import { PanelLeft } from 'lucide-react';

import { paraBuscar } from '@shared/ui/search-text';
import { useMemo, useState } from 'react';
import { Link, NavLink } from 'react-router';

import { useCurrentUser } from '@shared/session/use-session';
import { cn } from '@shared/ui/cn';
import { SearchInput } from '@shared/ui/input';
import { NAVIGATION, type NavGroup, type NavItem } from './navigation';

interface SidebarProps {
  readonly collapsed: boolean;
  readonly onToggle: () => void;
}

/**
 * Deja solo lo que el usuario puede abrir.
 *
 * Ofrecer una seccion que respondera 403 al pulsarla no es transparencia: es
 * hacerle perder el tiempo y sugerirle que algo no funciona. Un grupo que se
 * queda sin opciones desaparece entero, para que no quede un titulo suelto
 * sobre un hueco.
 *
 * Esto NO es control de acceso: la autorizacion la impone el servidor, que
 * lee los permisos del token firmado. Ocultar un enlace no impide navegar a
 * la ruta escribiendola.
 */
function visiblesPara(permisos: ReadonlySet<string> | undefined): readonly NavGroup[] {
  const puede = (item: NavItem): boolean =>
    item.permission === undefined || (permisos?.has(item.permission) ?? false);

  return NAVIGATION.map((group) => ({
    ...group,
    items: group.items.filter(puede),
  })).filter((group) => group.items.length > 0);
}

/**
 * Filtra el menu por lo tecleado.
 *
 * Existe por un motivo medible: el menu tiene veintiocho entradas repartidas
 * en ocho grupos, mas de las que caben en una pantalla, de modo que llegar a
 * "Inventario fisico" o a "Centros de costo" obliga a desplazar y leer. La
 * paleta de comandos (Ctrl+K) ya resolvia esto para quien conoce el atajo;
 * esto lo resuelve para quien no.
 *
 * Se conservan los grupos en lugar de aplanar los resultados: el grupo dice
 * de que parte del sistema es cada pantalla, y perderlo al filtrar obliga a
 * reconstruirlo de memoria.
 */
function filtrarPor(grupos: readonly NavGroup[], consulta: string): readonly NavGroup[] {
  const texto = paraBuscar(consulta);
  if (texto === '') return grupos;

  return grupos
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => paraBuscar(item.label).includes(texto)),
    }))
    .filter((group) => group.items.length > 0);
}

export function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const permitidos = visiblesPara(useCurrentUser()?.permissions);
  const [filtro, setFiltro] = useState('');
  const grupos = useMemo(() => filtrarPor(permitidos, filtro), [permitidos, filtro]);

  return (
    <aside
      className={cn(
        'z-20 flex shrink-0 flex-col border-r border-line bg-panel',
        'transition-[width] duration-(--t-panel) ease-(--ease-out)',
        collapsed ? 'w-(--spacing-rail-min)' : 'w-(--spacing-rail)',
      )}
    >
      <Brand collapsed={collapsed} />

      {/*
        El filtro y el conmutador comparten fila, arriba.
        El conmutador estaba al fondo del menu, a novecientos pixeles de la
        unica otra cosa con la que se usa. Contraido el filtro no se pinta -sin
        sitio para el texto seria una caja que no dice que hace ni deja leer lo
        que se escribe- y queda solo el icono, centrado.
      */}
      <div
        className={cn(
          'flex shrink-0 items-center gap-2 border-b border-line py-2.5',
          collapsed ? 'justify-center px-0' : 'px-3',
        )}
      >
        {!collapsed && (
          <SearchInput
            className="min-w-0 flex-1"
            value={filtro}
            onValueChange={setFiltro}
            placeholder="Filtrar menu"
            aria-label="Filtrar las opciones del menu"
            onKeyDown={(event) => {
              if (event.key === 'Escape') setFiltro('');
            }}
          />
        )}

        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-label={collapsed ? 'Expandir menu lateral' : 'Contraer menu lateral'}
          title={collapsed ? 'Expandir menu lateral' : 'Contraer menu lateral'}
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-md border border-line-strong',
            'bg-panel text-fg-subtle transition-colors duration-(--t-tap)',
            'hover:border-n300 hover:bg-sunken hover:text-fg-body',
          )}
        >
          <PanelLeft
            className={cn(
              'size-4 transition-transform duration-(--t-panel) ease-(--ease-out)',
              collapsed && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </button>
      </div>

      <nav
        aria-label="Navegacion principal"
        className="flex-1 overflow-y-auto overflow-x-hidden py-2"
      >
        {grupos.map((group) => (
          <div key={group.label} className="mb-4 last:mb-1">
            {collapsed ? (
              <div className="mx-3 mb-2 h-px bg-line" aria-hidden="true" />
            ) : (
              <p className="px-3.5 pb-2 font-display text-xs font-semibold uppercase tracking-[0.12em] text-fg-muted">
                {group.label}
              </p>
            )}
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <li key={item.to}>
                  <SidebarLink item={item} collapsed={collapsed} />
                </li>
              ))}
            </ul>
          </div>
        ))}

        {grupos.length === 0 && (
          <p className="px-3.5 py-6 text-sm leading-relaxed text-fg-subtle">
            Ninguna opcion coincide con <span className="font-medium text-fg-body">{filtro}</span>.
          </p>
        )}
      </nav>
    </aside>
  );
}

/**
 * Marca del sistema.
 *
 * Es un ENLACE a inicio. No lo era, y es lo primero que intenta cualquiera
 * que quiere volver al principio: un logotipo que no lleva a ninguna parte
 * obliga a buscar "Dashboard" en el menu.
 *
 * El monograma usa el mismo material que el boton primario -degradado del 6 %
 * y luz de canto de 1 px-. Por separado no se perciben; juntos hacen que la
 * pieza se lea como un objeto con volumen y no como un cuadrado de color. Que
 * la marca y la accion principal esten hechas de lo mismo es lo que hace que
 * el naranja se reconozca como una sola voz.
 *
 * Bajo el nombre va un filete naranja, el mismo gesto que marca la fila activa
 * en las tablas y la seccion activa en este menu. Repetir un unico motivo en
 * lugar de inventar uno por sitio es lo que sostiene la identidad.
 */
function Brand({ collapsed }: { collapsed: boolean }) {
  return (
    <Link
      to="/"
      aria-label="DOGITEC, ir al inicio"
      className={cn(
        'group flex h-(--spacing-header) shrink-0 items-center gap-3 border-b border-line',
        'transition-colors duration-(--t-tap) hover:bg-sunken',
        collapsed ? 'justify-center px-0' : 'px-3',
      )}
    >
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-lg',
          'bg-brand bg-linear-to-b from-white/25 to-transparent text-on-brand',
          'font-display text-2xl font-semibold leading-none',
          'shadow-[inset_0_1px_0_0_rgb(255_255_255/0.3),0_1px_2px_0_rgb(204_82_0/0.35)]',
          'transition-transform duration-(--t-ui) ease-(--ease-out) group-hover:scale-105',
        )}
        aria-hidden="true"
      >
        D
      </span>

      {!collapsed && (
        <span className="min-w-0">
          <span className="flex items-center gap-2">
            <span className="truncate font-display text-xl font-semibold leading-none tracking-tight text-fg">
              DOGITEC
            </span>
            <span className="h-3 w-0.5 shrink-0 rounded-full bg-brand" aria-hidden="true" />
          </span>
          <span className="mt-1.5 block truncate font-display text-[0.625rem] font-medium uppercase leading-none tracking-[0.16em] text-fg-subtle">
            Sistema logistico
          </span>
        </span>
      )}
    </Link>
  );
}

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'spine mx-2 flex h-9 items-center gap-3 rounded-md text-base',
          'transition-colors duration-(--t-tap)',
          // Contraido el icono se centra en el rail; expandido se alinea con
          // el texto. Conservar el relleno de la izquierda al contraer dejaba
          // la columna de iconos descuadrada respecto al monograma.
          collapsed ? 'justify-center px-0' : 'pl-3 pr-2.5',
          isActive
            ? 'bg-brand-tint font-medium text-brand-ink'
            : 'text-fg-body hover:bg-sunken hover:text-fg',
          item.pending === true && !isActive && 'text-fg-subtle',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* El lomo naranja se activa en CSS via aria-current, que NavLink ya
              anade al enlace activo: una fuente de verdad en lugar de dos.
              El icono acompana al estado: en una lista larga, el color del
              icono se ve antes que el peso de la letra. */}
          <Icon
            className={cn('size-4.5 shrink-0', isActive ? 'text-brand' : 'text-fg-subtle')}
            aria-hidden="true"
          />
          {!collapsed && <span className="flex-1 truncate">{item.label}</span>}
          {!collapsed && item.pending === true && (
            <span
              className="size-1.5 shrink-0 rounded-full bg-n300"
              title="Disponible en una fase posterior"
              aria-label="Disponible en una fase posterior"
            />
          )}
        </>
      )}
    </NavLink>
  );
}
